import { sessionUtils } from "@/lib/session-utils";
import api, { API_ENDPOINTS } from "@/src/api/api";
import { config } from "@/src/config/config";
import { AxiosRequestConfig } from "axios";

interface IScanPassword {
  file: File;
  type: string;
}

interface ICloudConfiguration {
  cloudProvider: string;
  cloudAccountId?: string;
  providerAlias?: string;
  region?: string;

  // AWS
  accessToken?: string;
  secretKey?: string;
  sessionToken?: string;

  // GCP
  gcpAuthType?: string;
  serviceAccountJson?: any;
  clientId?: string;
  clientSecret?: string;
  refreshToken?: string;

  // Microsoft 365
  m365ClientId?: string;
  m365ClientSecret?: string;
  m365Services?: string[];

  // GitHub
  githubAuthType?: string;
  githubAccessToken?: string;
  githubOAuthToken?: string;
  githubAppId?: string;
  githubAppPrivateKey?: string;

  // Kubernetes
  kubeconfigContent?: string;

  // IaC
  iacSourceType?: string;
  iacRepoUrl?: string;
  iacRepoToken?: string;

  // Docker / Image
  imageName?: string;
  registryUrl?: string;
  registryUsername?: string;
  registryPassword?: string;
}

const DEFAULT_REGIONS: Record<string, string> = {
  aws: "ap-south-1",
  gcp: "asia-south1",
  azure: "eastus",
};

const buildAddConfigurationPayload = (data: ICloudConfiguration): Record<string, any> => {
  const provider = (data.cloudProvider || "").toLowerCase();

  switch (provider) {
    case "aws":
      return {
        scan_type: "cloud",
        account_type: "AWS",
        account_id: data.cloudAccountId,
        access_key: data.accessToken,
        secret_key: data.secretKey,
        ...(data.sessionToken ? { session_token: data.sessionToken } : {}),
        region: data.region || DEFAULT_REGIONS.aws,
        ...(data.providerAlias ? { provider_alias: data.providerAlias } : {}),
      };

    case "gcp":
      return {
        scan_type: "cloud",
        account_type: "GCP",
        account_id: data.cloudAccountId,
        region: data.region || DEFAULT_REGIONS.gcp,
        ...(data.providerAlias ? { provider_alias: data.providerAlias } : {}),
      };

    case "azure":
      return {
        scan_type: "cloud",
        account_type: "AZURE",
        account_id: data.cloudAccountId,
        region: data.region || DEFAULT_REGIONS.azure,
        ...(data.providerAlias ? { provider_alias: data.providerAlias } : {}),
      };

    case "iac":
      return {
        scan_type: "iac",
        iac_source_type: data.iacSourceType || "git",
        iac_repo_url: data.iacRepoUrl,
        ...(data.iacRepoToken ? { iac_repo_token: data.iacRepoToken } : {}),
      };

    case "docker":
    case "image":
      return {
        scan_type: "image",
        image_name: data.imageName,
        ...(data.registryUrl ? { registry_url: data.registryUrl } : {}),
        ...(data.registryUsername ? { registry_username: data.registryUsername } : {}),
        ...(data.registryPassword ? { registry_password: data.registryPassword } : {}),
      };

    case "microsoft365":
      return {
        scan_type: "cloud",
        account_type: "M365",
        account_id: data.cloudAccountId,
        m365_tenant_id: data.cloudAccountId,
        m365_client_id: data.m365ClientId,
        m365_client_secret: data.m365ClientSecret,
        m365_services: Array.isArray(data.m365Services) ? data.m365Services : [],
      };

    case "github": {
      const base = {
        scan_type: "cloud",
        account_type: "GITHUB",
        account_id: data.cloudAccountId,
        ...(data.providerAlias ? { provider_alias: data.providerAlias } : {}),
      };
      if (data.githubAuthType === "oauth") {
        return { ...base, auth_type: "OAUTH_APP", oauth_token: data.githubOAuthToken };
      }
      if (data.githubAuthType === "app") {
        return {
          ...base,
          auth_type: "GITHUB_APP",
          app_id: data.githubAppId,
          private_key: data.githubAppPrivateKey,
        };
      }
      return {
        ...base,
        auth_type: "PERSONAL_ACCESS_TOKEN",
        access_token: data.githubAccessToken,
      };
    }

    case "kubernetes":
      return {
        scan_type: "cloud",
        account_type: "KUBERNETES",
        account_id: data.cloudAccountId,
        auth_type: "KUBECONFIG",
        kubeconfig_content: data.kubeconfigContent,
      };

    default:
      throw new Error(`Unsupported provider: ${data.cloudProvider}`);
  }
};

//  const BASE_URL_LG = "http://192.168.0.18:8009";
const BASE_URL_CSPM = config.API_URL + "/cspm_service";
// const BASE_URL_CSPM = "http://agmd.bitxiatech.com:8013";

export const CSPMService = {
  addCloudConfiguration: async (data: ICloudConfiguration) => {
    try {
      const payload = buildAddConfigurationPayload(data);

      const response = await api.post(
        `${BASE_URL_CSPM}${API_ENDPOINTS.ADD_CLOUD_CONFIGURATION}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error adding cloud configuration:", error);
      throw error;
    }
  },

  getAllCloudProviders: async (page: number = 1, pageSize: number = 10, filter_by?: string, search?: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_CSPM}${API_ENDPOINTS.GET_ALL_CLOUD_PROVIDERS}`,
        {
          params: {
            page,
            page_size: pageSize,
            filter_by,
            search,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching cloud providers:", error);
      throw error;
    }
  },
    getOverViewData: async () => {
    try {
      const response = await api.get(`${BASE_URL_CSPM}/api/v1/cspm-scan/overview_data`, {
        headers: { Accept: "application/json" },
      });
//       const response ={
//     "success": true,
//     "status": "200",
//     "message": "Dashboard data retrieved successfully",
//     "data": {
//         "recent_stats_data": {
//             "total_findings": 1368,
//             "connected_clouds": 1,
//             "critical_issues": 2,
//             "compliant_resources": 84,
//             "non_compliant_resources": 163,
//             "total_resources": 236,
//             "scan_statistics": {
//                 "total_scans": 3,
//                 "last_scan_date": "2025-11-21T08:29:19.862000",
//                 "average_findings": 456.0,
//                 "trend": "stable"
//             },
//             "risk_stats": {
//                 "risk_score": 0.0,
//                 "level": "CRITICAL",
//                 "description": "Immediate action required"
//             }
//         },
//         "over_all_stats_data": {
//             "overall_compliant_resources": 687,
//             "overall_non_compliant_resources": 681,
//             "overall_total_resources": 236
//         },
//         "recent_misconfigs": {
//             "scan_id": "692022dfda8a2e263fe87032",
//             "recent_scan_stats": {
//                 "critical": 42213,
//                 "high": 1,
//                 "medium": 11295,
//                 "low": 622,
//                 "informational": 0
//             },
//             "findings_by_status": {
//                 "total_findings": 460,
//                 "sucess": 230,
//                 "failure": 230,
//                 "no_change": 0,
                
//             },
//             "recent_critical_misconfigs": [
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_cassandra_exposed_to_internet-211125505517-us-east-1-3deee7142",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_cassandra_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to Cassandra ports (TCP 7000, 7001, 7199, 9042, 9160).",
//                     "Description": "Instance i-047f36dbdd56d4a35 does not have Cassandra ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "CSPM"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.4 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.4 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP ports 7000, 7001, 7199, 9042 or 9160.",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_cassandra_exposed_to_internet-211125505517-us-east-1-77ad19c24",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_cassandra_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to Cassandra ports (TCP 7000, 7001, 7199, 9042, 9160).",
//                     "Description": "Instance i-0091add6df545d192 does not have Cassandra ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "test Server"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.4 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.4 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP ports 7000, 7001, 7199, 9042 or 9160.",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_cifs_exposed_to_internet-211125505517-us-east-1-3deee7142",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_cifs_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to TCP port 139 or 445 (CIFS).",
//                     "Description": "Instance i-047f36dbdd56d4a35 does not have CIFS ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "CSPM"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.6.6 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "CIS-5.0 5.1.2",
//                             "C5-2025 PI-01.01AC",
//                             "CIS-4.0.1 5.1.2",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.6.6 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "CIS-5.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "CIS-4.0.1"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP port 139 or 445 (CIFS).",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_cifs_exposed_to_internet-211125505517-us-east-1-77ad19c24",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_cifs_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to TCP port 139 or 445 (CIFS).",
//                     "Description": "Instance i-0091add6df545d192 does not have CIFS ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "test Server"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.6.6 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "CIS-5.0 5.1.2",
//                             "C5-2025 PI-01.01AC",
//                             "CIS-4.0.1 5.1.2",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.6.6 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "CIS-5.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "CIS-4.0.1"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP port 139 or 445 (CIFS).",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_elasticsearch_kibana_exposed_to_internet-211125505517-us-east-1-3deee7142",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_elasticsearch_kibana_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to Elasticsearch and Kibana ports (TCP 9200, 9300, 5601).",
//                     "Description": "Instance i-047f36dbdd56d4a35 does not have Elasticsearch/Kibana ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "CSPM"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.6.3 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.6.3 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP ports 9200, 9300, 5601.",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_elasticsearch_kibana_exposed_to_internet-211125505517-us-east-1-77ad19c24",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_elasticsearch_kibana_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to Elasticsearch and Kibana ports (TCP 9200, 9300, 5601).",
//                     "Description": "Instance i-0091add6df545d192 does not have Elasticsearch/Kibana ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "test Server"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.6.3 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.6.3 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP ports 9200, 9300, 5601.",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_ftp_exposed_to_internet-211125505517-us-east-1-3deee7142",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_ftp_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to TCP port 20 or 21 (FTP)",
//                     "Description": "Instance i-047f36dbdd56d4a35 does not have FTP ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "CSPM"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.6.6 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.6.6 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP port 20 or 21 (FTP).",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_ftp_exposed_to_internet-211125505517-us-east-1-77ad19c24",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_ftp_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to TCP port 20 or 21 (FTP)",
//                     "Description": "Instance i-0091add6df545d192 does not have FTP ports open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "test Server"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.6.6 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.6.6 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group to remove the rule that allows ingress from the internet to TCP port 20 or 21 (FTP).",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_kafka_exposed_to_internet-211125505517-us-east-1-3deee7142",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_kafka_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to TCP port 9092 (Kafka).",
//                     "Description": "Instance i-047f36dbdd56d4a35 does not have Kafka port 9092 open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-047f36dbdd56d4a35",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "CSPM"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group associated with the EC2 instance to remove the rule that allows ingress from the internet to TCP port 9092 (Kafka).",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 },
//                 {
//                     "SchemaVersion": "2018-10-08",
//                     "Id": "prowler-ec2_instance_port_kafka_exposed_to_internet-211125505517-us-east-1-77ad19c24",
//                     "ProductArn": "arn:aws:securityhub:us-east-1::product/prowler/prowler",
//                     "RecordState": "ACTIVE",
//                     "ProductFields": {
//                         "ProviderName": "Prowler",
//                         "ProviderVersion": "5.14.0",
//                         "ProwlerResourceName": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192"
//                     },
//                     "GeneratorId": "prowler-ec2_instance_port_kafka_exposed_to_internet",
//                     "AwsAccountId": "211125505517",
//                     "Types": [
//                         "Infrastructure Security"
//                     ],
//                     "FirstObservedAt": "2025-11-21T08:15:33Z",
//                     "UpdatedAt": "2025-11-21T08:15:33Z",
//                     "CreatedAt": "2025-11-21T08:15:33Z",
//                     "Severity": {
//                         "Label": "CRITICAL"
//                     },
//                     "Title": "Ensure no EC2 instances allow ingress from the internet to TCP port 9092 (Kafka).",
//                     "Description": "Instance i-0091add6df545d192 does not have Kafka port 9092 open to the Internet.",
//                     "Resources": [
//                         {
//                             "Type": "AwsEc2Instance",
//                             "Id": "arn:aws:ec2:us-east-1:211125505517:instance/i-0091add6df545d192",
//                             "Partition": "aws",
//                             "Region": "us-east-1",
//                             "Tags": {
//                                 "Name": "test Server"
//                             }
//                         }
//                     ],
//                     "Compliance": {
//                         "Status": "PASSED",
//                         "RelatedRequirements": [
//                             "KISA-ISMS-P-2023-korean 2.6.1 2.6.2 2.10.2",
//                             "ProwlerThreatScore-1.0 2.1.6",
//                             "C5-2025 PI-01.01AC",
//                             "ISO27001-2022 A.8.20 A.8.21 A.8.22",
//                             "SOC2 cc_6_6",
//                             "NIS2 6.7.2.g",
//                             "KISA-ISMS-P-2023 2.6.1 2.6.2 2.10.2"
//                         ],
//                         "AssociatedStandards": [
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023-korean"
//                             },
//                             {
//                                 "StandardsId": "ProwlerThreatScore-1.0"
//                             },
//                             {
//                                 "StandardsId": "C5-2025"
//                             },
//                             {
//                                 "StandardsId": "ISO27001-2022"
//                             },
//                             {
//                                 "StandardsId": "SOC2"
//                             },
//                             {
//                                 "StandardsId": "NIS2"
//                             },
//                             {
//                                 "StandardsId": "KISA-ISMS-P-2023"
//                             }
//                         ]
//                     },
//                     "Remediation": {
//                         "Recommendation": {
//                             "Text": "Modify the security group associated with the EC2 instance to remove the rule that allows ingress from the internet to TCP port 9092 (Kafka).",
//                             "Url": "https://docs.aws.amazon.com/vpc/latest/userguide/VPC_SecurityGroups.html"
//                         }
//                     }
//                 }
//             ]
//         },
//         "misconfiguration_trends": [
//             {
//                 "date": "2025-10-23",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-24",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-25",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-26",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-27",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-28",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-29",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-30",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-10-31",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-01",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-02",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-03",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-04",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-05",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-06",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-07",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-08",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-09",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-10",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-11",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-12",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-13",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-14",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-15",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-16",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-17",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-18",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-19",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-20",
//                 "total_scans": 0,
//                 "average_severity_breakdown": {
//                     "critical": 0,
//                     "high": 0,
//                     "medium": 0,
//                     "low": 0,
//                     "informational": 0
//                 }
//             },
//             {
//                 "date": "2025-11-21",
//                 "total_scans": 3,
//                 "average_severity_breakdown": {
//                     "critical": 9.0,
//                     "high": 33.78,
//                     "medium": 42.77,
//                     "low": 14.48,
//                     "informational": 0.0
//                 }
//             }
//         ]
//     }
// }
      return response.data;
    } catch (error) {
      console.error("Error fetching overview data:", error);
      throw error;
    }
  },
    getScanFindings: async (scanId: string | number, page: number = 0, pageSize: number = 20) => {
      try {
        const response = await api.get(
          `${BASE_URL_CSPM}/api/v1/cspm-scan/scans-results/${scanId}`,
          {
            params: {
              // finding_type: findingType,
              page,
              page_size: pageSize,
            },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching scan findings:", error);
        throw error;
      }
    },
    scanSingleCloudProvider: async (data: any) => {
      try {
        const frameworks = data?.compliance_frameworks;
        const payload: Record<string, any> = {
          id: data?._id,
        };

        // Only include the field when the user picked specific frameworks.
        // Skipping leaves it out so the backend runs its default coverage.
        if (Array.isArray(frameworks) && frameworks.length > 0) {
          payload.compliance_frameworks = frameworks;
        }

        const response = await api.post(
          `${BASE_URL_CSPM}${API_ENDPOINTS.SCAN_SINGLE_CLOUD_PROVIDER}`,
          payload
        );
//     const response =  {
//     "status": "200",
//     "success": true,
//     "message": "Scan completed",
//     "provider": "aws",
//     "region": "ap-south-1",
//     "scan_id": "6939112a4b5864c3e2a10cd6"
// }
        return response.data;
      } catch (error) {
        console.error("Error scanning cloud provider:", error);
        throw error;
      }
    },

  getComplianceFrameworks: async (
    cloudProvider: string
  ): Promise<Record<string, string[]>> => {
    try {
      const provider = (cloudProvider || "").toLowerCase();
      if (!provider) {
        throw new Error("Cloud provider is required");
      }
      const response = await api.get(
        `${BASE_URL_CSPM}${API_ENDPOINTS.GET_COMPLIANCE_FRAMEWORKS}/${provider}`,
        { headers: { accept: "*/*" } }
      );
      const grouped = response?.data?.grouped;
      return grouped && typeof grouped === "object" ? grouped : {};
    } catch (error) {
      console.error("Error fetching compliance frameworks:", error);
      throw error;
    }
  },

  deleteCloudProvider: async (config_id: string, reason: string) => {
    try {
      const response = await api.put(
        `${BASE_URL_CSPM}${API_ENDPOINTS.DELETE_CLOUD_PROVIDER}`,
        {
          config_id: config_id,
          reason: reason
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting cloud provider:", error);
      throw error;
    }
  },

   getAllFindings: async (
    id: string,
    page: number = 1, 
    pageSize: number = 10,
    finding_type?: string,
    severity?: string,
    status?: string,
    q?: string,
  ) => {
    try {
      const response = await api.get(
        `${BASE_URL_CSPM}${API_ENDPOINTS.GET_ALL_FINDINGS}/${id}/findings`,
        {
          params: {
            page,
            page_size: pageSize,
            finding_type,
            severity,
            status,
            q,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching findings:", error);
      throw error;
    }
  },

  getAllCompliances: async (id: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_CSPM}${API_ENDPOINTS.GET_ALL_COMPLIANCES}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching compliances:", error);
      throw error;
    }
  },

  getComplianceDetails: async (scanId: string, complianceId: string, page: number = 1, pageSize: number = 10, status?: string, severity?: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_CSPM}${API_ENDPOINTS.GET_COMPLIANCE_DETAILS}/${scanId}/${complianceId}`,
        {
          params: {
            page,
            page_size: pageSize,
            status,
            severity,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching compliance details:", error);
      throw error;
    }
  },

 getAllResources: async (scanId: string, page: number = 1, pageSize: number = 10, search?: string) => {
  try {
    const response = await api.get(
      `${BASE_URL_CSPM}/api/v1/cspm-scan/scans/${scanId}/resources`,
      {
        params: {
          page,
          page_size: pageSize,
          ...(search && { search }), // Only include search param if it exists
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching resources:", error);
    throw error;
  }
},
  getAllNotifications: async (page: number = 1, pageSize: number = 10, status?: string) => {
    try {
      const response = await api.get(
        `${config.API_URL}/dspm_service/dspm/deletion-requests`,
        {
          params: {
            page,
            page_size: pageSize,
            ...(status && { status })
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  },

  rejectNotification: async (notificationId: string, reason: string) => {
    try {
      const response = await api.post(
        `${BASE_URL_CSPM}/api/v1/cspm-scan/deletion-requests/${notificationId}/reject`,
        {
          reason,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error rejecting notification:", error);
      throw error;
    }
  },
  approveNotification: async (notificationId: string, payload?: { export_zip: boolean }) => {
    const response = await api.post(
      `${BASE_URL_CSPM}/api/v1/cspm-scan/deletion-requests/${notificationId}/approve`,
      payload,
      {
        responseType: payload?.export_zip ? "arraybuffer" : "json", // ← key fix
      }
    );
    return response.data;
  },

  // ==================== Attack Path APIs ====================
  
  /**
   * Sync attack path environment for a specific account
   * This is a long-running operation (20-25 minutes)
   * @param accountId - The cloud provider account ID (from account_id field)
   * @returns sync_id, sync_tag, account_id, completed_at
   */
  syncAttackPath: async (accountId: string) => {
    try {
      const response = await api.post(
        `${BASE_URL_CSPM}/api/v1/attack-paths/sync/${accountId}`,
        {},
        {
          headers: { 
            accept: "application/json"
          },
          timeout: 3000000, // 30 minutes timeout
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error syncing attack path:", error);
      throw error;
    }
  },

  /**
   * Get available attack path queries
   * @returns Object with query names as keys and descriptions as values
   */
  getAttackPathQueries: async () => {
    try {
      const response = await api.get(
        `${BASE_URL_CSPM}/api/v1/attack-paths/queries`,
        {
          headers: { 
            accept: "application/json"
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching attack path queries:", error);
      throw error;
    }
  },

  /**
   * Run a specific attack path query
   * @param queryName - The name of the query to execute
   * @param accountId - The cloud provider account ID
   * @returns Query results with principals, policies, actions, and resources
   */
  runAttackPathQuery: async (queryName: string, accountId: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_CSPM}/api/v1/attack-paths/run/${queryName}`,
        {
          params: {
            account_id: accountId
          },
          headers: { 
            accept: "application/json"
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error running attack path query:", error);
      throw error;
    }
  },
};

// ==================== TypeScript Interfaces ====================

export interface IAttackPathSyncResponse {
  success: boolean;
  status: string;
  message: string;
  data: {
    sync_id: string;
    sync_tag: number;
    account_id: string;
    completed_at: string;
  };
}

export interface IAttackPathQueriesResponse {
  success: boolean;
  status: string;
  message: string;
  data: {
    [queryName: string]: string; // query name -> description
  };
}

// Flexible interface to handle different query response structures
export interface IAttackPathQueryResult {
  // Common IAM query fields
  principal?: string;
  policy?: string | null;
  actions?: string[];
  resources?: string[];
  
  // EC2/Instance query fields
  instance?: string;
  public_ip?: string | null;
  role_name?: string;
  role_arn?: string;
  
  // Lateral movement query fields
  source_arn?: string;
  source_type?: string[];
  target_role?: string;
  target_role_arn?: string;
  
  // Additional metadata
  severity?: string;
  [key: string]: any; // Allow for additional dynamic fields
}

export interface IAttackPathQueryResponse {
  success: boolean;
  status: string;
  message: string;
  data: {
    query: string;
    description: string;
    sync_tag: number;
    results: IAttackPathQueryResult[];
  };
}